"use client";
import { useEffect, useRef, useState } from "react";
import { ClockCircleOutlined, WarningOutlined } from "@ant-design/icons";

const SESSION_DURATION_MS = 10 * 60 * 1000;

export default function SessionTimer({ createdAt, onExpire }) {
  const [remaining, setRemaining] = useState(null);
  const expiredRef = useRef(false);

  useEffect(() => {
    if (!createdAt) return;
    expiredRef.current = false;

    const tick = () => {
      const left = Math.max(0, SESSION_DURATION_MS - (Date.now() - createdAt));
      setRemaining(left);
      if (left === 0 && !expiredRef.current) {
        expiredRef.current = true;
        clearInterval(id);
        onExpire?.();
      }
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [createdAt]);

  if (!createdAt || remaining === null) return null;

  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  const expired = remaining === 0;
  const isUrgent = remaining < 2 * 60 * 1000;
  const isWarning = remaining < 5 * 60 * 1000;

  const theme =
    expired || isUrgent
      ? { bg: "#fff2f0", border: "#ffccc7", text: "#cf1322", icon: "#ff4d4f" }
      : isWarning
        ? { bg: "#fff7e6", border: "#ffd591", text: "#d46b08", icon: "#fa8c16" }
        : {
            bg: "#f6ffed",
            border: "#b7eb8f",
            text: "#389e0d",
            icon: "#52c41a",
          };

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 14px",
        borderRadius: 10,
        border: `1px solid ${theme.border}`,
        backgroundColor: theme.bg,
        marginBottom: 4,
      }}
    >
      {expired || isUrgent ? (
        <WarningOutlined
          style={{ color: theme.icon, fontSize: 16, flexShrink: 0 }}
        />
      ) : (
        <ClockCircleOutlined
          style={{ color: theme.icon, fontSize: 16, flexShrink: 0 }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        {expired ? (
          <span style={{ color: theme.text, fontWeight: 600, fontSize: 13 }}>
            Session expired — redirecting back to seat selection…
          </span>
        ) : (
          <span style={{ color: theme.text, fontSize: 13 }}>
            <span style={{ fontWeight: 700 }}>
              Seats reserved for{" "}
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {String(minutes).padStart(2, "0")}:
                {String(seconds).padStart(2, "0")}
              </span>
            </span>
            <span style={{ fontWeight: 400, marginLeft: 6, opacity: 0.8 }}>
              — complete your purchase before time runs out
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
